import type { BusinessResiliencyFrameworkResult } from '../types.js';
import { S14_SCENARIOS, type S14Intake } from './s14Workflow.js';

const text = (value: unknown, maximum = 16000) => String(value ?? '').trim().slice(0, maximum);
const impact = (value: unknown) => {
    const clean = text(value, 30);
    return ['Critical', 'High', 'Medium', 'Low', 'Needs verification'].includes(clean) ? clean : 'Needs verification';
};

export function cleanS14Report(source: any, intake: S14Intake,
    preserveVerification = false): BusinessResiliencyFrameworkResult {
    const raw = source && typeof source === 'object' ? source : {};
    const rows: any[] = Array.isArray(raw.biaMatrix) ? raw.biaMatrix : [];
    return {
        reportType: 'BusinessResiliencyFrameworkResult', workflowVersion: 'BRP-14-v2',
        organizationName: intake.organizationName, industry: intake.industry, sites: intake.sites,
        executiveSummary: text(raw.executiveSummary), businessImpactAnalysis: text(raw.businessImpactAnalysis),
        biaMatrix: intake.criticalFunctions.map(item => {
            const row = rows.find(candidate => text(candidate?.functionName, 160).toLowerCase() === item.name.toLowerCase()) || {};
            const scenarioImpacts: Record<string, string> = {};
            const scenarioMitigations: Record<string, string> = {};
            const scenarioVerified: Record<string, boolean> = {};
            for (const scenario of S14_SCENARIOS)
            {
                scenarioImpacts[scenario] = impact(row.scenarioImpacts?.[scenario]);
                scenarioMitigations[scenario] = text(item.scenarioMitigations?.[scenario], 1000);
                scenarioVerified[scenario] = preserveVerification && row.scenarioVerified?.[scenario] === true
                    && !!scenarioMitigations[scenario] && scenarioImpacts[scenario] !== 'Needs verification';
            }
            return { functionName: item.name, rtoHours: Number(item.rtoHours), mtpdHours: Number(item.mtpdHours),
                strategy: item.strategy, scenarioImpacts, scenarioMitigations, scenarioVerified,
                dependencies: text(row.dependencies, 2000),
                impactRationale: text(row.impactRationale, 2000) };
        }),
        recoveryStrategies: text(raw.recoveryStrategies), alternateLocations: text(raw.alternateLocations),
        bcmTeam: text(raw.bcmTeam), supplierManagement: text(raw.supplierManagement),
        legalRegulatory: text(raw.legalRegulatory), activation: text(raw.activation),
        maintenance: text(raw.maintenance),
        evidenceGaps: [...new Set((Array.isArray(raw.evidenceGaps) ? raw.evidenceGaps : []).slice(0, 25)
            .map((item: unknown): string => text(item, 500)).filter(Boolean))] as string[],
        timestamp: text(raw.timestamp, 50) || new Date().toISOString(),
    };
}

export function missingS14Report(report: BusinessResiliencyFrameworkResult): string[] {
    const sections = [
        ['Executive Summary', report.executiveSummary], ['Business Impact Analysis', report.businessImpactAnalysis],
        ['Recovery Strategies', report.recoveryStrategies], ['Alternate Locations', report.alternateLocations],
        ['BCM Team', report.bcmTeam], ['Supplier Management', report.supplierManagement],
        ['Legal and Regulatory', report.legalRegulatory], ['Activation', report.activation],
        ['Maintenance', report.maintenance],
    ];
    const missing = sections.filter(([, value]) => !text(value)).map(([name]) => name);
    if (!report.biaMatrix.length || report.biaMatrix.some(row => !row.dependencies || !row.impactRationale
        || S14_SCENARIOS.some(scenario => !row.scenarioImpacts[scenario])))
        missing.push('Business Impact Matrix');
    return missing;
}

export function missingS14Verification(report: BusinessResiliencyFrameworkResult): string[] {
    const missing: string[] = [];
    for (const row of report.biaMatrix)
        for (const scenario of S14_SCENARIOS)
            if (!row.scenarioMitigations?.[scenario] || row.scenarioImpacts?.[scenario] === 'Needs verification'
                || row.scenarioVerified?.[scenario] !== true)
                missing.push(`${row.functionName}: ${scenario}`);
    return missing;
}

export function s14ReviewError(previous: string, next: string, report: BusinessResiliencyFrameworkResult,
    changed: boolean): string | null {
    if (next === 'Approved' && previous !== 'Validated')
        return 'Validate the S-14 plan before approval and sign-off.';
    const missing = missingS14Report(report);
    if (next !== 'Draft' && missing.length)
        return `Complete the report: ${missing.slice(0, 3).join(', ')}.`;
    if (next !== 'Draft' && missingS14Verification(report).length)
        return `Verify every disruption cell before ${next.toLowerCase()}.`;
    if (next === 'Approved' && changed)
        return 'Save and validate report changes before approval.';
    return null;
}

export function s14DeliveryLink(requestId: string, reference: string) {
    const id = `service-${requestId}-s14`;
    return { id, sourceId: id, title: `${reference} Business Resiliency Framework`,
        type: 'BusinessResiliencyFrameworkResult' as const };
}
